import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { makePostApiCall } from "../../../shared/makeApiCall";
import { normalizeLowStockThreshold } from "../../../shared/lowStockThreshold";
import {
  downloadProductBarcodePdf,
  getProductBarcodePdf,
  ProductBarcodeApiError,
} from "../../../shared/productBarcodeApi";
import { useToast } from "../../../contexts/ToastContext";

// ...existing imports
import ModelErrorComponent from "../../../components/modals/ModelErrorComponent";
import type { EmptyModalState } from "../../../components/modals/EmptyModal";
import type { SlideOverState } from "../../../components/slideOver/SlideOver";
import type {
  Option,
  Product,
  FormValues,
} from "../../../components/forms/products/ProductDetailsForm";
import type { PriceListItem } from "../../../components/forms/products/ProductPriceList";
import type { ApiMessage } from "../../../types/api";
import type { ProductMutationResponse, ProductPayload } from "../../../types/products";

export function ProductDetailsViewModel(logout: () => void, productId: string) {
  const navigate = useNavigate();
  const { showSuccess } = useToast();
  const [emptyModal, setEmptyModelOpen] = useState<EmptyModalState>({
    open: false,
    childComponent: null,
  });
  const [loading, setLoading] = useState(true);
  const [product, setProduct] = useState<Product | null>(null);
  const [primaryCategoryOptions, setPrimaryCategoryOptions] = useState<Option[]>([]);
  const [subCategoryOptions, setSubCategoryOptions] = useState<Option[]>([]);
  const [usersOptions, setUsersOptions] = useState<Option[]>([]);
  const [priceList, setPriceList] = useState<PriceListItem[]>([]);
  const [printingBarcode, setPrintingBarcode] = useState(false);

  const [slideOver, setSlideOver] = useState<SlideOverState>({
    childComponent: null,
    open: false,
  });

  const showErrorModal = (title: string, subtitle = "Please try again.") => {
    setEmptyModelOpen({
      childComponent: (
        <ModelErrorComponent setOpen={setEmptyModelOpen} subtitle={subtitle} title={title} />
      ),
      open: true,
    });
  };

  const getProduct = async function (search: string | null = null, page = 1) {
    setLoading(true);
    const payload = {
      page: page,
      search: search,
    };
    const callResult = await makePostApiCall<Product, typeof payload>(
      `products/${productId}`,
      payload
    );
    if (callResult.success) {
      const data = callResult.data;
      const n = Number(data.unit_cost);
      const productPayload = {
        ...data,
        unit_cost: Number.isFinite(n) ? n : "",
      };
      setProduct(productPayload);

      const pc = productPayload.primary_category;
      const primaryCatId =
        pc != null && pc.value != null && pc.value !== ""
          ? Number(pc.value)
          : productPayload.category_id != null && productPayload.category_id !== ""
            ? Number(productPayload.category_id)
            : NaN;

      if (Number.isFinite(primaryCatId) && primaryCatId > 0) {
        await getSubCategoriesByPrimary(primaryCatId);
      } else {
        setSubCategoryOptions([]);
      }
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
    setLoading(false);
  };

  const updateProduct = async function (values: FormValues) {
    let parsedLowStock: string | undefined;
    try {
      parsedLowStock = normalizeLowStockThreshold(values.low_stock_threshold);
    } catch (error) {
      showErrorModal("Could not update product", (error as Error).message);
      return;
    }
    const editableValues = { ...values };
    delete editableValues.stock;
    delete editableValues.category_id;
    delete editableValues.sub_category_id;

    const payload: ProductPayload = {
      ...editableValues,
      // Empty strings survive multipart serialization so cleared categories reach Laravel.
      primary_category: values.primary_category?.value ?? "",
      sub_category: values.sub_category?.value ?? "",
      low_stock_threshold: parsedLowStock,
      track_shelf_life: values.track_shelf_life ? 1 : 0,
      shelf_life: values.track_shelf_life
        ? Number.parseInt(String(values.shelf_life ?? 0), 10) || 0
        : 0,
      priceList: priceList,
    };
    // Log the endpoint and payload for debugging before the API call
    try {
      const safePayload = { ...payload };
      // avoid logging large file objects if present
      if (Array.isArray(safePayload.image1)) {
        safePayload.image1 = "<File: image1>";
      }
      if (Array.isArray(safePayload.image2)) {
        safePayload.image2 = "<File: image2>";
      }
      console.log("[updateProduct] POST", `updateProduct/${productId}`, safePayload);
    } catch (err) {
      console.log("[updateProduct] Error preparing payload log", err);
    }
    const callResult = await makePostApiCall<ProductMutationResponse, ProductPayload>(
      `updateProduct/${productId}`,
      payload,
      "multipart/form-data"
    );

    if (callResult.success) {
      showSuccess(callResult.data.message || "Product updated");
      setSlideOver({
        childComponent: null,
        open: false,
      });
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      const errMsg =
        callResult.data && callResult.data.message
          ? callResult.data.message
          : "Could not update product";
      showErrorModal("Could not update product", errMsg);
    }
  };

  const createProduct = async function (values: FormValues) {
    let parsedLowStock: string | undefined;
    try {
      parsedLowStock = normalizeLowStockThreshold(values.low_stock_threshold);
    } catch (error) {
      showErrorModal("Could not create product", (error as Error).message);
      return;
    }
    const editableValues = { ...values };
    delete editableValues.stock;
    delete editableValues.category_id;
    delete editableValues.sub_category_id;

    const payload: ProductPayload = {
      ...editableValues,
      // Empty strings survive multipart serialization so cleared categories reach Laravel.
      primary_category: values.primary_category?.value ?? "",
      sub_category: values.sub_category?.value ?? "",
      low_stock_threshold: parsedLowStock,
      track_shelf_life: values.track_shelf_life ? 1 : 0,
      shelf_life: values.track_shelf_life
        ? Number.parseInt(String(values.shelf_life ?? 0), 10) || 0
        : 0,
      priceList: priceList,
    };

    try {
      const safePayload = { ...payload };
      if (Array.isArray(safePayload.image1)) {
        safePayload.image1 = "<File: image1>";
      }
      if (Array.isArray(safePayload.image2)) {
        safePayload.image2 = "<File: image2>";
      }
    } catch (err) {
      console.log("[createProduct] Error preparing payload log", err);
    }

    const callResult = await makePostApiCall<ProductMutationResponse, ProductPayload>(
      `createProduct`,
      payload,
      "multipart/form-data"
    );

    if (callResult.success) {
      showSuccess(callResult.data.message || "Product created");
      window.location.assign(`/products/${callResult.data.id}`);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      const errMsg =
        callResult.data && callResult.data.message
          ? callResult.data.message
          : "Could not create product";
      showErrorModal("Could not create product", errMsg);
    }
  };

  const getPriceListForProduct = async function () {
    setLoading(true);

    const callResult = await makePostApiCall<PriceListItem[]>(`pricelist/${productId}`);
    setLoading(false);
    if (callResult.success) {
      setPriceList(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Could not fetch product list");
    }
  };

  const getPrimaryCategoriesForDropdown = async function () {
    setLoading(true);

    const callResult = await makePostApiCall<Option[]>(`products/categories/dropdown`);
    setLoading(false);
    if (callResult.success) {
      setPrimaryCategoryOptions(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Could not fetch categories");
    }
  };

  const getUsersForDropdown = async function () {
    setLoading(true);

    const callResult = await makePostApiCall<Option[]>(`usersForDropdown`);
    setLoading(false);
    if (callResult.success) {
      setUsersOptions(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Could not fetch users");
    }
  };

  const getSubCategoriesByPrimary = async function (categoryId: number) {
    const callResult = await makePostApiCall<Option[]>(
      `/products/subcategories/dropdown/${categoryId}`
    );
    if (callResult.success) {
      setSubCategoryOptions(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Could not fetch subcategories");
    }
  };

  const deleteProductImage = async function (imageType: "image1" | "image2") {
    const payload = {
      imageType: imageType,
    };

    try {
      const safePayload = { ...payload };
      console.log("[deleteProductImage] POST", `delete/${productId}/${imageType}`, safePayload);
    } catch (err) {
      console.log("[deleteProductImage] Error preparing payload log", err);
    }

    const callResult = await makePostApiCall<ApiMessage, typeof payload>(
      `delete/${productId}/${imageType}`,
      payload
    );

    if (callResult.success) {
      showSuccess(callResult.data.message || "Image deleted");
      return true;
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      const errMsg =
        callResult.data && callResult.data.message
          ? callResult.data.message
          : "Could not delete image";
      showErrorModal("Could not delete image", errMsg);
      return false;
    }
  };

  const printBarcode = async function () {
    if (!productId || productId === "create") return;

    setPrintingBarcode(true);
    try {
      const { blob, filename } = await getProductBarcodePdf(productId);
      downloadProductBarcodePdf(blob, filename);
      showSuccess("Barcode PDF downloaded");
    } catch (error: unknown) {
      if (error instanceof ProductBarcodeApiError && error.status === 401) {
        logout();
        return;
      }

      showErrorModal(
        "Could not print barcode",
        error instanceof ProductBarcodeApiError
          ? error.message
          : "Could not generate the product barcode PDF."
      );
    } finally {
      setPrintingBarcode(false);
    }
  };

  return {
    primaryCategoryOptions,
    getPrimaryCategoriesForDropdown,
    updateProduct,
    getProduct,
    slideOver,
    setSlideOver,
    product,
    loading,
    emptyModal,
    setEmptyModelOpen,
    getUsersForDropdown,
    usersOptions,
    getSubCategoriesByPrimary,
    subCategoryOptions,
    getPriceListForProduct,
    priceList,
    setPriceList,
    createProduct,
    deleteProductImage,
    printBarcode,
    printingBarcode,
  };
}
